import { createContext, useContext, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GameContext = createContext(null);

const initialGameState = {
  // Game setup
  playlist: null,
  totalInnings: 9,
  teamA: { name: '', players: [], score: 0 },
  teamB: { name: '', players: [], score: 0 },
  
  // Game state
  currentInning: 1,
  isTopInning: true, // true = Team A batting (top), false = Team B batting (bottom)
  battingTeam: 'A',
  pitchingTeam: 'B',
  outs: 0,
  bases: { first: false, second: false, third: false },
  
  // Batting order tracking
  teamABatterIndex: 0,
  teamBBatterIndex: 0,
  
  // Player stats
  playerStats: {}, // { playerId: { singles, doubles, triples, homeRuns, outs } }
  
  // Linked accounts for local multiplayer XP tracking
  linkedAccounts: {}, // { 'A-0': { token, userId, username }, 'B-1': ... }
  
  // Songs tracking
  usedSongIds: [],
  songs: [],
  currentSong: null,
  
  // Game flow
  gameStarted: false,
  gameEnded: false,
  winner: null,
  
  // Inning history for summary
  inningHistory: [], // [{ inning, half, events: [{ batter, result, runsScored }] }]
  currentInningEvents: [],
};

export const GameProvider = ({ children }) => {
  const [gameState, setGameState] = useState(initialGameState);

  const resetGame = useCallback(() => {
    setGameState(initialGameState);
  }, []);

  const setupGame = useCallback((playlist, innings, teamA, teamB, songs) => {
    const playerStats = {};
    
    teamA.players.forEach((player, idx) => {
      playerStats[`A-${idx}`] = { 
        name: player, 
        team: 'A',
        singles: 0, doubles: 0, triples: 0, homeRuns: 0, outs: 0 
      };
    });
    
    teamB.players.forEach((player, idx) => {
      playerStats[`B-${idx}`] = { 
        name: player, 
        team: 'B',
        singles: 0, doubles: 0, triples: 0, homeRuns: 0, outs: 0 
      };
    });

    setGameState(prev => ({
      ...prev,
      playlist,
      totalInnings: innings,
      teamA: { ...teamA, score: 0 },
      teamB: { ...teamB, score: 0 },
      songs: [...songs],
      playerStats,
      gameStarted: true,
    }));
  }, []);

  const setBattingFirst = useCallback((team) => {
    setGameState(prev => ({
      ...prev,
      battingTeam: team,
      pitchingTeam: team === 'A' ? 'B' : 'A',
      isTopInning: team === 'A',
    }));
  }, []);

  const getCurrentBatter = useCallback(() => {
    const team = gameState.battingTeam;
    const index = team === 'A' ? gameState.teamABatterIndex : gameState.teamBBatterIndex;
    const players = team === 'A' ? gameState.teamA.players : gameState.teamB.players;
    return {
      name: players[index],
      id: `${team}-${index}`,
      index,
    };
  }, [gameState]);

  const getRandomSong = useCallback(() => {
    const availableSongs = gameState.songs.filter(
      song => !gameState.usedSongIds.includes(song.id)
    );
    
    if (availableSongs.length === 0) {
      return null;
    }
    
    const randomIndex = Math.floor(Math.random() * availableSongs.length);
    const song = availableSongs[randomIndex];
    
    setGameState(prev => ({
      ...prev,
      currentSong: song,
      usedSongIds: [...prev.usedSongIds, song.id],
    }));
    
    return song;
  }, [gameState.songs, gameState.usedSongIds]);

  // Pending result state for async processing
  const [pendingResult, setPendingResult] = useState(null);

  const recordAtBat = useCallback(async (correctGuesses) => {
    const batter = getCurrentBatter();
    
    // Call backend API to process at-bat using the authoritative server-side logic
    try {
      const response = await fetch(`${API_URL}/api/game/process-at-bat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bases: gameState.bases,
          outs: gameState.outs,
          half: gameState.isTopInning ? 'top' : 'bottom',
          team_a_score: gameState.teamA.score,
          team_b_score: gameState.teamB.score,
          correct_guesses: correctGuesses,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process at-bat');
      }

      const data = await response.json();
      
      // Map API result to display format
      const resultDisplayMap = {
        'OUT': 'OUT',
        'SINGLE': 'SINGLE',
        'DOUBLE': 'DOUBLE',
        'TRIPLE': 'TRIPLE',
        'HOME_RUN': 'HOME RUN',
      };
      const displayResult = resultDisplayMap[data.result] || data.result;

      setGameState(prev => {
        const newState = { ...prev };
        
        // Update player stats
        const statsKey = batter.id;
        const stats = { ...prev.playerStats[statsKey] };
        
        if (data.result === 'OUT') {
          stats.outs++;
          newState.outs = data.outs;
        } else if (data.result === 'SINGLE') {
          stats.singles++;
        } else if (data.result === 'DOUBLE') {
          stats.doubles++;
        } else if (data.result === 'TRIPLE') {
          stats.triples++;
        } else if (data.result === 'HOME_RUN') {
          stats.homeRuns++;
        }
        
        newState.playerStats = {
          ...prev.playerStats,
          [statsKey]: stats,
        };
        
        // Update scores from server response
        newState.teamA = { ...prev.teamA, score: data.team_a_score };
        newState.teamB = { ...prev.teamB, score: data.team_b_score };
        
        // Update bases from server response (the authoritative state)
        newState.bases = data.bases;
        
        // Track event
        newState.currentInningEvents = [
          ...prev.currentInningEvents,
          { 
            batter: batter.name, 
            batterId: batter.id,
            result: displayResult, 
            runsScored: data.runs_scored,
            song: prev.currentSong,
          }
        ];
        
        // Advance to next batter
        if (prev.battingTeam === 'A') {
          newState.teamABatterIndex = (prev.teamABatterIndex + 1) % prev.teamA.players.length;
        } else {
          newState.teamBBatterIndex = (prev.teamBBatterIndex + 1) % prev.teamB.players.length;
        }
        
        // Clear current song
        newState.currentSong = null;
        
        return newState;
      });

      return { 
        result: displayResult, 
        runsScored: data.runs_scored, 
        basesAdvanced: correctGuesses,
        isGrandSlam: data.is_grand_slam,
      };
    } catch (error) {
      console.error('Error processing at-bat:', error);
      // Fallback: still advance batter but show error
      setGameState(prev => ({
        ...prev,
        teamABatterIndex: prev.battingTeam === 'A' 
          ? (prev.teamABatterIndex + 1) % prev.teamA.players.length 
          : prev.teamABatterIndex,
        teamBBatterIndex: prev.battingTeam === 'B' 
          ? (prev.teamBBatterIndex + 1) % prev.teamB.players.length 
          : prev.teamBBatterIndex,
        currentSong: null,
      }));
      return { result: 'ERROR', runsScored: 0, basesAdvanced: 0, isGrandSlam: false };
    }
  }, [getCurrentBatter, gameState.bases, gameState.outs, gameState.isTopInning, gameState.teamA.score, gameState.teamB.score]);

  const checkEndOfHalfInning = useCallback(() => {
    return gameState.outs >= 3;
  }, [gameState.outs]);

  const endHalfInning = useCallback(() => {
    setGameState(prev => {
      const inningRecord = {
        inning: prev.currentInning,
        half: prev.isTopInning ? 'top' : 'bottom',
        battingTeam: prev.battingTeam,
        events: [...prev.currentInningEvents],
        teamAScore: prev.teamA.score,
        teamBScore: prev.teamB.score,
      };
      
      const newHistory = [...prev.inningHistory, inningRecord];
      
      // Check if game should end
      const isBottomOfLastInning = !prev.isTopInning && prev.currentInning >= prev.totalInnings;
      const isTopOfNextInning = prev.isTopInning && prev.currentInning >= prev.totalInnings;
      
      // Check for walk-off or game end conditions
      let gameEnded = false;
      let winner = null;
      
      if (isBottomOfLastInning) {
        gameEnded = true;
        winner = prev.teamA.score > prev.teamB.score ? prev.teamA.name : 
                 prev.teamB.score > prev.teamA.score ? prev.teamB.name : 'TIE';
      } else if (isTopOfNextInning) {
        // After top of last inning, check if we need bottom
        // (Game continues to bottom unless already won)
      }
      
      if (!prev.isTopInning && prev.currentInning >= prev.totalInnings) {
        gameEnded = true;
        winner = prev.teamA.score > prev.teamB.score ? prev.teamA.name : 
                 prev.teamB.score > prev.teamA.score ? prev.teamB.name : 'TIE';
      }
      
      return {
        ...prev,
        inningHistory: newHistory,
        currentInningEvents: [],
        outs: 0,
        bases: { first: false, second: false, third: false },
        isTopInning: !prev.isTopInning,
        battingTeam: prev.battingTeam === 'A' ? 'B' : 'A',
        pitchingTeam: prev.pitchingTeam === 'A' ? 'B' : 'A',
        currentInning: prev.isTopInning ? prev.currentInning : prev.currentInning + 1,
        gameEnded,
        winner,
      };
    });
  }, []);

  const setLinkedAccounts = useCallback((accounts) => {
    setGameState(prev => ({
      ...prev,
      linkedAccounts: accounts || {},
    }));
  }, []);

  const value = {
    ...gameState,
    resetGame,
    setupGame,
    setBattingFirst,
    getCurrentBatter,
    getRandomSong,
    recordAtBat,
    checkEndOfHalfInning,
    endHalfInning,
    setLinkedAccounts,
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
